import {
    type ChangeEvent,
    type ComponentProps,
    type FocusEvent,
    type ReactNode,
    useCallback,
} from 'react';
import { FormField, InputGroup, Paragraph, TextInput } from '@george-labs.com/design-system';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props<FormValues> extends FormFieldProps<FormValues> {
    autoFocus?: boolean;
    prepend?: ReactNode;
    append?: ReactNode;
    type?: 'text' | 'password';
    maxLength?: number;
    inputProps?: Partial<ComponentProps<typeof TextInput>>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
    trimOnBlur?: boolean;
}

const LABEL_COLUMN_WIDTH = 4;

export function FormTextField<FormValues = void>({
    label,
    placeholder,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelPosition,
    fieldName,
    required,
    disabled,
    info,
    warning,
    autoFocus,
    append,
    prepend,
    description,
    type,
    maxLength,
    inputProps,
    isErrorVisible = true,
    showErrorBeforeBlur,
    addon,
    shouldClearOnUnmount,
    alert,
    trimOnBlur = false,
    className,
    compact,
}: Props<FormValues>) {
    const [fieldProps, { error, touched, dirty }, { setValue }] = useField(fieldName, {
        shouldClearOnUnmount,
    });

    const isErrorFieldVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            fieldProps.onChange(event);
            inputProps?.onChange?.(event);
        },
        [inputProps, fieldProps],
    );
    const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            if (trimOnBlur) {
                const trimmedValue = event.target.value.trim();
                if (trimmedValue !== fieldProps.value) {
                    setValue(trimmedValue);
                    event.target.value = trimmedValue;
                }
            }
            fieldProps.onBlur(event);
            inputProps?.onBlur?.(event);
        },
        [fieldProps, inputProps, trimOnBlur, setValue],
    );

    return (
        <FormField
            description={description}
            required={required}
            input={
                <InputGroup disabled={disabled} append={append} prepend={prepend}>
                    <TextInput
                        {...inputProps}
                        {...fieldProps}
                        type={type}
                        autoFocus={autoFocus}
                        data-cy={dataCy}
                        maxLength={maxLength}
                        placeholder={placeholder || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </InputGroup>
            }
            label={label}
            placeholder={placeholder}
            error={
                isErrorFieldVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            labelPosition={labelPosition}
            disabled={disabled}
            warning={warning}
            info={info}
            addon={addon}
            alert={alert}
            className={className}
            compact={compact}
        />
    );
}

FormTextField.LABEL_POSITION = FormField.LABEL_POSITION;
FormTextField.TYPE = TextInput.TYPE;
